import assetUtils, { Asset } from 'app-lib/assetUtils'
import { sleep } from 'app-lib/asyncUtils'
import web3Utils from 'web3-utils'
import * as ethers from 'ethers5'
import { BigNumber } from '@0x/utils'
import {
  AnyProvider,
  getDependenciesStrict,
  getDependenciesStrictRead,
  getDependenciesStrictAsset,
  runTx,
} from '../providerUtils'

export const INFINITE_ALLOWANCE = new BigNumber(2).pow(256).minus(1) // MAX_UINT

/**
 * Get spending approvals amount for a spender for a single **token (addresses)**
 */
async function getSpendingApproval(
  provider: AnyProvider,
  token: string | Asset,
  spender: string | Asset
) {
  const { helperContract, chainId, userAddress } = getDependenciesStrict(provider)
  const tokenAddress = web3Utils.isAddress(token)
    ? token
    : assetUtils.getAddress(chainId, token as Asset)
  const spenderAddress = web3Utils.isAddress(spender)
    ? spender
    : assetUtils.getAddress(chainId, spender as Asset)

  const result = await helperContract
    .allowance([tokenAddress], userAddress, spenderAddress)
    .callAsync()

  return {
    amount: result[0],
    spender: spenderAddress,
    assetAddress: tokenAddress,
    token: web3Utils.isAddress(token)
      ? assetUtils.getAssetFromAddress(chainId, tokenAddress)
      : <Asset>token,
  }
}

/**
 * Get the spending approvals of one token for multiple spender contracts
 * eg: `getSpendingApprovalMultipleSpenders(USDCAdd, [bzxContractAdd, SushiContractAdd])`
 */
async function getSpendingApprovalMultipleSpenders(
  provider: AnyProvider,
  userAddress: string,
  tokenAddress: string,
  spendersAddresses: string[]
) {
  const { contractsSource } = getDependenciesStrictRead(provider)
  const erc20Contract = contractsSource.getErc20Contract(tokenAddress)
  const result: { amount: BigNumber; spender: string; assetAddress: string }[] = []
  for (const spender of spendersAddresses) {
    await sleep(100) // avoid issue with RPC throttling
    const amount = await erc20Contract.allowance(userAddress, spender).callAsync()
    result.push({
      amount,
      spender,
      assetAddress: tokenAddress,
    })
  }
  return result
}

/**
 * Get spending approvals amount for a spender for multiple **token (addresses)**
 */
async function getSpendingApprovals(
  provider: AnyProvider,
  tokenAddresses: string[],
  spenderAddress: string
) {
  const { helperContract, userAddress } = getDependenciesStrict(provider)
  const result = await helperContract
    .allowance(tokenAddresses, userAddress, spenderAddress)
    .callAsync()

  return result.map((item, index) => ({
    amount: item,
    spender: spenderAddress,
    tokenAddress: tokenAddresses[index],
  }))
}

/**
 * Get spending approvals amount for a spender for multiple **assets**
 * Useful if we want to use our Asset list as argument
 */
async function getSpendingApprovalsOfAssets(
  provider: AnyProvider,
  assets: Asset[],
  spenderAddress: string
) {
  const tokenAddresses = assets.map((asset) =>
    assetUtils.getAddress(provider.currentChainId, asset)
  )
  const approvals = await getSpendingApprovals(provider, tokenAddresses, spenderAddress)
  return approvals.map((approval, index) => ({
    amount: approval.amount,
    token: assets[index],
    assetAddress: approval.tokenAddress,
    spender: spenderAddress,
  }))
}

/**
 * Amount should be in base unit
 * Uses the async generator flow.
 */
async function setSpendingApprovalErc20(
  provider: AnyProvider,
  args: { erc20Token: string | Asset; spender: string | Asset; amount: BigNumber }
) {
  const { contractsSource, chainId } = getDependenciesStrict(provider)
  const erc20Contract = contractsSource.getErc20Contract(args.erc20Token)
  const spenderAddress = web3Utils.isAddress(args.spender)
    ? args.spender
    : assetUtils.getAddress(chainId, args.spender as Asset)

  const tx = erc20Contract.approve(spenderAddress, args.amount)
  return runTx(provider, tx, erc20Contract)
}

/**
 * Does not use 0x web3wrapper; fix because of USDT approval failing on ethereum
 */
async function setSpendingApprovalUnsafe(
  provider: AnyProvider,
  asset: Asset,
  spenderAddress: string,
  amount: BigNumber
) {
  const { web3Wrapper, erc20Contract } = getDependenciesStrictAsset(provider, asset)
  // @ts-ignore
  const ethersProvider = new ethers.providers.Web3Provider(web3Wrapper.getProvider())
  const erc20 = new ethers.Contract(
    erc20Contract.address,
    erc20Contract.abi,
    ethersProvider.getSigner()
  )
  const tx = await erc20.approve(spenderAddress, amount.toFixed())
  const mined = await ethersProvider.waitForTransaction(tx.hash, 1)
  if (mined.status !== 1) {
    throw new Error('Spending approval failed')
  }
  return {
    amount,
    token: asset,
    assetAddress: assetUtils.getAddress(provider.currentChainId, asset),
    spender: spenderAddress,
  }
}

/**
 * Set the spending allowance of a token for a specific spender (contract)
 */
async function setSpendingApproval(
  provider: AnyProvider,
  asset: Asset,
  spenderAddress: string,
  amount: BigNumber
) {
  const { erc20Contract, userAddress, web3Wrapper, chainId } = getDependenciesStrictAsset(
    provider,
    asset
  )
  if (chainId === 1 && asset === Asset.USDT) {
    return setSpendingApprovalUnsafe(provider, asset, spenderAddress, amount)
  }
  const txHash = await erc20Contract.approve(spenderAddress, amount).sendTransactionAsync({
    from: userAddress,
  })
  await web3Wrapper.awaitTransactionSuccessAsync(txHash, 5000)

  return {
    amount,
    token: asset,
    assetAddress: assetUtils.getAddress(provider.currentChainId, asset),
    spender: spenderAddress,
  }
}

/**
 * Set infinite spending allowance of a token for a specific spender (contract)
 */
async function setSpendingApprovalInfinite(
  provider: AnyProvider,
  asset: Asset,
  spenderAddress: string
) {
  return setSpendingApproval(provider, asset, spenderAddress, INFINITE_ALLOWANCE)
}

export default {
  getSpendingApproval,
  getSpendingApprovalMultipleSpenders,
  getSpendingApprovals,
  getSpendingApprovalsOfAssets,
  setSpendingApprovalErc20,
  setSpendingApproval,
  setSpendingApprovalInfinite,
}
